import { apiUrl, extractApiList } from './api'
import { getFarmPk } from './siloHomeVisibility'

function alignNextPage(next) {
  if (!next) return null
  const base = apiUrl('company_users/')
  if (base.startsWith('http://') && next.startsWith('https://')) return next.replace('https://', 'http://')
  if (base.startsWith('https://') && next.startsWith('http://')) return next.replace('http://', 'https://')
  return next
}

export const COMPANY_USER_ROLES = [
  { value: 'company_admin', label: 'Administrador da empresa' },
  { value: 'farm_admin', label: 'Administrador da granja' },
]

function settingsObject(record) {
  if (!record || typeof record !== 'object') return null
  let settings = record.settings !== undefined ? record.settings : record
  if (typeof settings === 'string') {
    try {
      settings = JSON.parse(settings)
    } catch (error) {
      return null
    }
  }
  return settings && typeof settings === 'object' ? settings : null
}

export function companyAdminUserManagementEnabled(companySettings) {
  const records = []
  if (Array.isArray(companySettings)) records.push(...companySettings)
  else if (companySettings?.results) records.push(...extractApiList(companySettings))
  else if (companySettings) records.push(companySettings)

  return records.some((record) => settingsObject(record)?.company_admin_user_management === true)
}

export function canManageCompanyUsers(user, companySettings) {
  return user?.role === 'company_admin' && companyAdminUserManagementEnabled(companySettings)
}

export function farmOptions(farms) {
  return (Array.isArray(farms) ? farms : [])
    .map((farm) => ({
      pk: getFarmPk(farm),
      name: farm?.name || '',
    }))
    .filter((farm) => farm.pk)
}

export function roleLabel(role) {
  return COMPANY_USER_ROLES.find((item) => item.value === role)?.label || role || ''
}

export function farmLabel(farms, associatedFarm) {
  if (associatedFarm == null) return '—'
  const match = farmOptions(farms).find((farm) => String(farm.pk) === String(associatedFarm))
  return match?.name || '—'
}

export function emptyCompanyUserForm() {
  return {
    username: '',
    password: '',
    password_confirm: '',
    email: '',
    first_name: '',
    last_name: '',
    role: 'company_admin',
    associated_farm: '',
  }
}

export function formFromUser(user) {
  return {
    username: user.username || '',
    password: '',
    password_confirm: '',
    email: user.email || '',
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    role: user.role || 'company_admin',
    associated_farm: user.role === 'farm_admin' && user.associated_farm != null
      ? String(user.associated_farm)
      : '',
  }
}

function associatedFarmValue(form) {
  if (form.role !== 'farm_admin') return null
  const pk = Number(form.associated_farm)
  return Number.isFinite(pk) && pk > 0 ? pk : null
}

export function validateCompanyUserForm(form, { editing }) {
  if (!String(form.first_name || '').trim()) return 'Informe o nome.'
  if (!String(form.last_name || '').trim()) return 'Informe o sobrenome.'
  if (!String(form.email || '').trim()) return 'Informe o e-mail.'
  if (!editing && !String(form.username || '').trim()) return 'Informe o usuário.'
  if (!editing && !form.password) return 'Informe a senha.'
  if (!editing && !form.password_confirm) return 'Confirme a senha.'
  if (form.password || form.password_confirm) {
    if (form.password !== form.password_confirm) return 'As senhas não conferem.'
  }
  if (form.role === 'farm_admin' && !associatedFarmValue(form)) return 'Selecione a granja.'
  if (form.role !== 'company_admin' && form.role !== 'farm_admin') return 'Papel inválido.'
  return ''
}

export function buildCreatePayload(form) {
  return {
    username: String(form.username).trim(),
    password: form.password,
    email: String(form.email).trim(),
    first_name: String(form.first_name).trim(),
    last_name: String(form.last_name).trim(),
    role: form.role,
    associated_farm: associatedFarmValue(form),
  }
}

function sameFarm(left, right) {
  if (left == null && right == null) return true
  return Number(left) === Number(right)
}

export function buildPatchPayload(form, original) {
  const next = {
    first_name: String(form.first_name).trim(),
    last_name: String(form.last_name).trim(),
    email: String(form.email).trim(),
    role: form.role,
    associated_farm: associatedFarmValue(form),
  }
  const patch = {}
  ;['first_name', 'last_name', 'email', 'role'].forEach((field) => {
    if (next[field] !== (original[field] || '')) patch[field] = next[field]
  })
  if (!sameFarm(next.associated_farm, original.associated_farm ?? null)) {
    patch.associated_farm = next.associated_farm
  }
  if (form.password) patch.password = form.password
  return patch
}

const FIELD_LABELS = {
  username: 'Usuário',
  password: 'Senha',
  email: 'E-mail',
  first_name: 'Nome',
  last_name: 'Sobrenome',
  role: 'Papel',
  associated_farm: 'Granja',
  detail: '',
  non_field_errors: '',
}

export function formatApiError(body) {
  if (!body || typeof body !== 'object') return 'Não foi possível concluir a operação.'
  if (typeof body.detail === 'string' && Object.keys(body).length === 1) return body.detail
  const parts = Object.entries(body).map(([key, value]) => {
    const text = Array.isArray(value) ? value.join(' ') : String(value)
    const label = FIELD_LABELS[key]
    if (label === undefined) return text
    return label ? `${label}: ${text}` : text
  }).filter(Boolean)
  return parts.join(' ') || 'Não foi possível concluir a operação.'
}

async function readBody(response) {
  if (response.status === 204) return null
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch (error) {
    return null
  }
}

export async function listCompanyUsers(headers) {
  const users = []
  const seen = new Set()
  let url = apiUrl('company_users/')

  while (url && !seen.has(url)) {
    seen.add(url)
    const response = await fetch(url, { headers })
    const body = await readBody(response)
    if (!response.ok) {
      return { ok: false, status: response.status, body, users }
    }
    users.push(...extractApiList(body))
    url = alignNextPage(body?.next)
  }

  return { ok: true, status: 200, users }
}

async function sendCompanyUser(url, method, headers, payload) {
  const response = await fetch(url, {
    method,
    headers: {
      ...headers,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
  const body = await readBody(response)
  return { status: response.status, body }
}

export async function createCompanyUser(headers, payload) {
  const result = await sendCompanyUser(apiUrl('company_users/'), 'POST', headers, payload)
  return {
    ok: result.status === 201,
    status: result.status,
    user: result.body,
    body: result.body,
  }
}

export async function updateCompanyUser(headers, pk, payload) {
  const result = await sendCompanyUser(apiUrl(`company_users/${pk}/`), 'PATCH', headers, payload)
  return {
    ok: result.status === 200,
    status: result.status,
    user: result.body,
    body: result.body,
  }
}

export async function deleteCompanyUser(headers, pk) {
  const response = await fetch(apiUrl(`company_users/${pk}/`), {
    method: 'DELETE',
    headers,
  })
  const body = await readBody(response)
  return { ok: response.status === 204, status: response.status, body }
}
